import type { ExperienceDto, PackageDto } from "@tripcart/shared";

type SeasonDto = NonNullable<ExperienceDto["seasons"]>[number];

/**
 * Overlay a season's per-category price overrides onto a package's FULL
 * category set.
 *
 * `season.packageOverrides[].categories` is intentionally SPARSE — the
 * traveler-api emits only the categories that carry a season price override;
 * every other category inherits its base price (see
 * `packages/shared/.../availability.dto.ts`). So we MERGE by category id: each
 * base category is replaced by its overridden version when the season provides
 * one, and otherwise kept as-is. The base order is preserved.
 *
 * Bug history: a previous REPLACE (`categories: override.categories`) dropped
 * every non-overridden category from the traveller picker, the price
 * breakdown, the capacity sum, and the booking whenever a selected date fell
 * in a season that overrode even one category — e.g. a Couple-only seasonal
 * price hid Adult/Senior/Teen entirely and silently discarded the traveller's
 * existing selection.
 */
export function applySeasonToPackage(
  pkg: PackageDto,
  season: SeasonDto | null,
): PackageDto {
  if (!season) return pkg;
  const override = season.packageOverrides.find((o) => o.packageId === pkg.id);
  if (!override || override.categories.length === 0) return pkg;
  const overrideById = new Map(override.categories.map((c) => [c.id, c]));
  return {
    ...pkg,
    categories: pkg.categories.map((base) => overrideById.get(base.id) ?? base),
  };
}
